import {
  Button,
  Modal,
  Form,
  Typography,
  message,
  DatePicker,
  Row,
  Col,
  Spin,
} from "antd";
import React, {
  forwardRef,
  useState,
  useImperativeHandle,
  useRef,
} from "react";
import { EditOutlined, BankOutlined } from "@ant-design/icons";
import http from "@/utils/http";
import api from "@/utils/api";
import CustomUploadNewBank from "@/hocComponents/customUploadNewBank";
import dayjs from "dayjs";

const UploadModal = forwardRef((props, ref) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [initLoading, setInitLoading] = useState(false);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [visible, setVisible] = useState(false);
  const [statementFiles, setStatementFiles] = useState([]);
  const [previewIndex, setPreviewIndex] = useState(0);
  const [filePaths, setFilePaths] = useState([]);
  const [btnDisabled, setBtnDisabled] = useState(false);
  const [uploadId, setUploadId] = useState(null);
  const [policyNumber, setPolicyNumber] = useState("");
  const initialServerFileUidsRef = useRef(new Set());
const uploadRef = useRef(null);

  const { onSuccess } = props;
  const columnStyle = {
    border: "1px solid #f0f0f0",
    padding: 20,
    borderRadius: 8,
    background: "#fafafa",
    minHeight: "100%",
  };

  const parseToDayjs = (value) => {
    if (!value) return null;
    if (Array.isArray(value)) {
      const [y, m, d] = value;
      if (!y || !m || !d) return null;
      return dayjs(
        `${y}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`,
      );
    }
    const d = dayjs(value);
    return d.isValid() ? d : null;
  };

  const closeModal = () => {
    setVisible(false);
    setUploadId(null);
    setPolicyNumber("");
    setStatementFiles([]);
    initialServerFileUidsRef.current = new Set();
    setPreviewIndex(0);
    setFilePaths([]);
    setBtnDisabled(false);
    form.resetFields();
  };

  useImperativeHandle(ref, () => ({
    open: async (record = {}) => {
      form.resetFields();
      setStatementFiles([]);
      initialServerFileUidsRef.current = new Set();
      setPreviewIndex(0);
      setFilePaths([]);
      setUploadId(record?.id || null);
      setPolicyNumber(record?.policyNumber || "");
      setVisible(true);
      setInitLoading(true);
      let expirationDate = parseToDayjs(record?.expirationDate);
      let effectiveDate = parseToDayjs(record?.effectiveDate);
      let files = [];

      try {
        if (record?.id) {
          const res = await http.get(api.getInsuranceById, {
            params: { id: record.id },
          });
          const data = res?.data || {};
          expirationDate = parseToDayjs(data?.expirationDate) || expirationDate;
          effectiveDate = parseToDayjs(data?.effectiveDate) || effectiveDate;

          // if (data.insuranceFileList?.length) {
          //   files = data.insuranceFileList.map((f) => ({
          //     uid: String(f.id),
          //     id: f.id,
          //     name: f.fileName,
          //     fileName: f.fileName,
          //     status: "done",
          //     type: f.type,
          //     url: f.url,
          //     allPath: f.url,
          //     path: f.path,
          //     suffix: f.suffix,
          //     _fromServer: true,
          //   }));
          // }
              // 统一处理所有文件
    const allFiles = [];

    // 处理保单文件 insuranceFileList
    if (data.insuranceFileList?.length) {
      const policyFiles = data.insuranceFileList.map((f) => ({
        uid: String(f.id),
        id: f.id,
        name: f.fileName,
        fileName: f.fileName,
        status: "done",
        type: f.type ,
        url: f.url,
        allPath: f.url,
        path: f.path,
        suffix: f.suffix,
        _fromServer: true,
      }));
      allFiles.push(...policyFiles);
    }

    // 处理支付文件 insurancePaymentFileList
    if (data.insurancePaymentFileList?.length) {
      const paymentFiles = data.insurancePaymentFileList.map((f) => ({
        uid: String(f.fileUploadId || f.uuid || f.id),
        id: f.fileUploadId || f.uuid || f.id,
        fileUploadId: f.fileUploadId,
        name: f.name || f.fileName,
        fileName: f.name || f.fileName,
        status: "done",
        type: f.type ,
        url: f.base64 || f.url,  
        allPath: f.base64 || f.url,
        path: f.path,
        suffix: f.ext || f.suffix,
        foreignId: f.foreignId,
        _fromServer: true,
      }));
      allFiles.push(...paymentFiles);
    }

    // 设置合并后的文件列表
    files = allFiles;
        }
      } catch (err) {
        message.error("Failed to load insurance detail.");
      } finally {
        setInitLoading(false);
      }

      setStatementFiles(files);
      setFilePaths(files);
      setTimeout(() => {
  if (files.length > 0) {
    uploadRef.current?.initUpdateList(files);
    uploadRef.current?.setPreviewIndex?.(0);
  }
}, 0);
      initialServerFileUidsRef.current = new Set(
        files.map((item) => item?.uid).filter(Boolean),
      );
      form.setFieldsValue({
        expirationDate,
        effectiveDate,
        fileList: files,
      });
    },
  }));

  const syncFileList = (files = []) => {
    setFilePaths(files);
  };

  const addOldFn = async (files) => {
    setBtnDisabled(true);
    try {
      const currentFile = files?.[files.length - 1];
      if (!currentFile?.originFileObj) {
        return { flag: false };
      }

      const uploadedFile = await uploadByBankApi(currentFile);
      const normalizedFile = {
        ...uploadedFile,
        uid: uploadedFile?.customId || uploadedFile?.uuid || currentFile.uid,
        type: "bankStatement",
        name:
          uploadedFile?.fileDateName ||
          uploadedFile?.fileName ||
          currentFile.name,
        fileName:
          uploadedFile?.fileDateName ||
          uploadedFile?.fileName ||
          currentFile.name,
        path: uploadedFile?.path || "",
        url: uploadedFile?.url || uploadedFile?.allPath || "",
        allPath:
          uploadedFile?.url ||
          uploadedFile?.allPath ||
          (currentFile.originFileObj &&
            URL.createObjectURL(currentFile.originFileObj)),
        originFileObj: currentFile.originFileObj,
        size: currentFile.size,
      };

      return {
        flag: true,
        fileData: normalizedFile,
      };
    } catch (error) {
      console.error(error);
      message.error(error?.message || "Upload failed");
      return { flag: false };
    } finally {
      setBtnDisabled(false);
    }
  };

  const deleteOldFn = async () => true;

  const deleteBase64FileFn = async (file) =>
    filePaths.filter((item) =>
      file?.uuid ? item.uuid !== file.uuid : item.uid !== file.uid,
    );

  React.useEffect(() => {
    const normalizedFiles = (filePaths || []).map((item) => ({
      ...item,
      path: item?.path || "",
      fileName: item?.fileName || item?.name || "",
    }));

    setStatementFiles(normalizedFiles);
    setPreviewIndex(normalizedFiles.length ? 0 : -1);
    form.setFieldsValue({
      fileList: normalizedFiles,
      bankFile: filePaths,
    });
  }, [filePaths, form]);

  const uploadByBankApi = async (file) => {
    const formData = new FormData();
    formData.append("files", file.originFileObj);
    try {
      setUploadLoading(true);
      const res = await http.post(api.getInsuranceFile, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (res.code === "1") return res.data[0];
      throw new Error(res.msg || "Upload failed");
    } finally {
      setUploadLoading(false);
    }
  };

  // 提交
  const onFinish = async () => {
    setLoading(true);
    try {
      const values = await form.validateFields();
      const { expirationDate, effectiveDate } = values;

      const fileList = values.fileList || [];
      const initialServerFileUids = initialServerFileUidsRef.current;
      const insuranceFileList = fileList
        .filter((item) => !initialServerFileUids.has(item?.uid))
        .map((item) => {
          const path = item?.path || "";
          const fallbackName = path ? path.split("/").pop() : "";
          return {
            path,
            fileName: item?.fileName || item?.name || fallbackName,
            status: 1,
          };
        })
        .filter((item) => item.path);

      const params = {
        id: uploadId,
        expirationDate: expirationDate?.format("YYYY-MM-DD"),
        effectiveDate: effectiveDate?.format("YYYY-MM-DD"),
        insuranceFileList,
      };
      const res = await http.post(api.getInstanceUpload, params);

      if (res?.code === "1") {
        message.success("Upload successfully.");
        closeModal();
        onSuccess && onSuccess();
      } else {
        message.error(res?.msg);
      }
    } catch (err) {
      console.error(err);
      if (!err?.errorFields) {
        message.error("Request failed, please try again");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title={
        <Row justify="start" align="middle" className="model_title">
          <Col>
            <EditOutlined className="model_title_mr10" />
          </Col>
          <Col>
            <Typography.Title level={4} className="mg">
              UPLOAD POLICY
            </Typography.Title>
          </Col>
        </Row>
      }
      width="80%"
      style={{
        maxHeight: "70vh",
      }}
      open={visible}
      onCancel={closeModal}
      maskClosable={false}
      footer={
        <div style={{ display: "flex", justifyContent: "center", gap: "16px" }}>
          <Button onClick={closeModal}>Cancel</Button>
          <Button
            type="primary"
            loading={loading || uploadLoading || initLoading}
            onClick={() => form.submit()}
          >
            Submit
          </Button>
        </div>
      }
    >
      <Spin spinning={initLoading}>
        <Row>
          <div className="model_top" style={{ width: "100%" }}>
            <Form
              form={form}
              name="createBankForm"
              layout="horizontal"
              labelCol={{ flex: "120px" }}
              wrapperCol={{ flex: 1 }}
              style={{ width: "100%" }}
              onFinish={onFinish}
            >
              <Row gutter={24} style={{ width: "100%", margin: 0 }}>
                {/* Insurance Detail */}
                <Col span={5}>
                  <div style={columnStyle}>
                    <div className="modal-section-title">
                      <BankOutlined /> Insurance Detail
                    </div>
                    <div style={{ marginBottom: 12 }}>
                      Policy Number: {policyNumber || "-"}
                    </div>
                    <Form.Item
                      name="expirationDate"
                      label="Expiration Date"
                      rules={[{ required: true }]}
                    >
                      <DatePicker style={{ width: "100%" }} />
                    </Form.Item>
                    <Form.Item
                      name="effectiveDate"
                      label="Effective Date"
                      rules={[{ required: true }]}
                    >
                      <DatePicker style={{ width: "100%" }} />
                    </Form.Item>
                  </div>
                </Col>

                {/* Upload Section */}
                <Col flex="19">
                  <div style={columnStyle}>
                    <div
                      style={{
                        marginBottom: "16px",
                        fontWeight: "bold",
                        fontSize: "20px",
                      }}
                    >
                      Upload PDF image
                    </div>
                    <Form.Item
                      labelCol={{ span: 0 }}
                      wrapperCol={{ span: 24 }}
                      label={null}
                    >
                      <div>
                        <div className="uploadText">
                          Uploaded files only support JPG, PNG or PDF.
                        </div>
                        <div
                          style={{ marginTop: "20px", display: "flex", gap: 16 }}
                        >
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <Form.Item name="bankFile" noStyle>
                              <CustomUploadNewBank
                               ref={uploadRef}
                                customConfig={{
                                  uploadButtonStyle: {
                                    top: "0",
                                  },
                                  pdfHeight: "62vh",
                                  fileFullName: "name",
                                  notMultiple: true,
                                  accept: ".jpg, .jpeg, .png, .pdf",
                                  status: "add",
                                  deleteOldFn: deleteOldFn,
                                  deleteBase64FileFn: deleteBase64FileFn,
                                  addOldFn: addOldFn,
                                }}
                                setFilePaths={syncFileList}
                                filePaths={filePaths}
                                setBtnDisabled={setBtnDisabled}
                                btnDisabled={btnDisabled}
                              />
                            </Form.Item>
                          </div>
                        </div>
                      </div>

                      <Form.Item name="fileList" hidden />
                    </Form.Item>
                  </div>
                </Col>
              </Row>
            </Form>
          </div>
        </Row>
      </Spin>
    </Modal>
  );
});
export default UploadModal;
